<script lang="ts" setup>
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import Dropdown from 'primevue/dropdown'
import Button from 'primevue/button'
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { Icon } from '@iconify/vue'
import type { KnittingSetOut } from '@/types/product-types'
import { generateUniqueId } from '@/constants'
import { addKnittingSetOutToProduct, editKnittingSetOutForProduct, deleteKnittingSetOutFromProduct, getProductById } from '@/api/products'
import { useProductSpecificationForm } from '@/composables/useProductSpecificationForm'

const props = defineProps<{
    knittingOut?: KnittingSetOut[]
}>()

const router = useRouter()
const { loading, showToast } = useProductSpecificationForm('view')
const editingRowId = ref<string | null>(null)
const knittingSetOuts = ref<(KnittingSetOut & { isNew?: boolean })[]>(props.knittingOut || [])
const originalRowData = ref<KnittingSetOut | null>(null)
const productId = router.currentRoute.value.params.id as string
const isAddingNew = ref<boolean>(false)

const feeders = [
  { name: '1', value: 1 },
  { name: '2', value: 2 },
  { name: '3', value: 3 },
  { name: '4', value: 4 },
  { name: '5', value: 5 }
]

const dialNeedles = [
  { name: '1', value: 1 },
  { name: '2', value: 2 },
  { name: '3', value: 3 },
  { name: '4', value: 4 },
  { name: '5', value: 5 }
]

const isRowEditable = (rowId?: string) => {
  return editingRowId.value === rowId
}

const handleEnableEdit = (row: KnittingSetOut) => {
  originalRowData.value = JSON.parse(JSON.stringify(row))
  editingRowId.value = row.id!
}

const addNewKnittingSetOutRow = () => {
  const newId = generateUniqueId()
  const newRow: KnittingSetOut & { isNew?: boolean } = {
    id: newId,
    feeder: 1,
    dialNeedlesBack: 1,
    dialNeedlesFront: 1,
    cylinderNeedlesTop1: 1,
    cylinderNeedlesTop2: 1,
    cylinderNeedlesTop3: 1,
    cylinderNeedlesBottom4: 1,
    isNew: true,
  }

  knittingSetOuts.value.push(newRow)
  editingRowId.value = newId
  isAddingNew.value = true
}

const handleSave = async (id?: string) => {
  if (!id) return
  const item = knittingSetOuts.value.find((item) => item.id === id)
  if (!item) return

  editingRowId.value = null
  isAddingNew.value = false

  const payload: KnittingSetOut = {
    id: item.id,
    feeder: item.feeder,
    dialNeedlesBack: item.dialNeedlesBack,
    dialNeedlesFront: item.dialNeedlesFront,
    cylinderNeedlesTop1: item.cylinderNeedlesTop1,
    cylinderNeedlesTop2: item.cylinderNeedlesTop2,
    cylinderNeedlesTop3: item.cylinderNeedlesTop3,
    cylinderNeedlesBottom4: item.cylinderNeedlesBottom4,
  }

  try {
    if (item.isNew) {
      await addKnittingSetOutToProduct(productId, payload)
      showToast('success', 'Success', 'Knitting Set Out added successfully')
    } else {
      await editKnittingSetOutForProduct(productId, payload, id)
      showToast('success', 'Success', 'Knitting Set Out updated successfully')
    }

    await loadKnittingSetOuts()
  } catch (error: any) {
    console.error('Error saving knitting set out:', error)
    showToast('error', 'Error', 'Failed to save knitting set out')

    if (originalRowData.value) {
      const index = knittingSetOuts.value.findIndex(item => item.id === id)
      if (index !== -1) {
        knittingSetOuts.value[index] = { ...originalRowData.value }
      }
    }
  }
}

const handleDelete = (id: string) => {
  deleteKnittingSetOut(id)
}

const deleteKnittingSetOut = async (id: string) => {
  try {
    await deleteKnittingSetOutFromProduct(productId, id)
    showToast('success', 'Success', 'Knitting Set Out deleted successfully')
    await loadKnittingSetOuts()
  } catch (error: any) {
    console.error('Error deleting knitting set out:', error)
    showToast('error', 'Error', 'Failed to delete knitting set out')
  }
}

const loadKnittingSetOuts = async () => {
  try {
    const product = await getProductById(productId)
    if (product.knittingSetOuts && product.knittingSetOuts.length > 0) {
      knittingSetOuts.value = product.knittingSetOuts.map(item => ({ ...item }))
    } else {
      knittingSetOuts.value = []
    }
  } catch (error) {
    console.error('Error loading knitting set outs:', error)
  }
}

onMounted(async () => {
  await loadKnittingSetOuts()
})
</script>

<template>
  <DataTable
        :value="knittingSetOuts"
        paginator
        :rows="10"
        table-style="min-width: 50rem"
        striped-rows
        paginator-template="PrevPageLink CurrentPageReport NextPageLink"
        :rows-per-page-options="[10, 25]"
        current-page-report-template="Showing {first} to {last} of {totalRecords}"
        :loading="loading"
        :pt="{
         table:{ class: [{ 'min-h-[30rem]': loading }]}
        }"
        :pt-options="{
          mergeProps: true
        }"
      >
        <Column field="feeder" header="Feeder">
          <template #body="slotProps">
            <div class="flex flex-col">
              <p class="invisible">Back:</p>
              <Dropdown
                v-model="slotProps.data.feeder"
                :options="feeders"
                option-label="name"
                option-value="value"
                :disabled="!isRowEditable(slotProps.data.id)"
                placeholder="Select Feeder"
              />
            </div>
          </template>
        </Column>
        <Column field="dialNeedles" header="Dial Needles">
          <template #body="slotProps">
            <div class="flex flex-row gap-8">
              <div class="flex flex-col">
                <p>Back:</p>
                <Dropdown
                  v-model="slotProps.data.dialNeedlesBack"
                  :options="dialNeedles"
                  option-label="name"
                  option-value="value"
                  :disabled="!isRowEditable(slotProps.data.id)"
                  placeholder="Select"
                />
              </div>
              <div class="flex flex-col">
                <p>Front:</p>
                <Dropdown
                  v-model="slotProps.data.dialNeedlesFront"
                  :options="dialNeedles"
                  option-label="name"
                  option-value="value"
                  :disabled="!isRowEditable(slotProps.data.id)"
                  placeholder="Select"
                />
              </div>
            </div>
          </template>
        </Column>
        <Column field="cylinderNeedles" header="Cylinder Needles">
          <template #body="slotProps">
            <div class="flex flex-row gap-8">
              <div class="flex flex-col">
                <p>Top 1:</p>
                <Dropdown
                  v-model="slotProps.data.cylinderNeedlesTop1"
                  :options="dialNeedles"
                  option-label="name"
                  option-value="value"
                  :disabled="!isRowEditable(slotProps.data.id)"
                  placeholder="Select"
                />
              </div>
              <div class="flex flex-col">
                <p>Top 2:</p>
                <Dropdown
                  v-model="slotProps.data.cylinderNeedlesTop2"
                  :options="dialNeedles"
                  option-label="name"
                  option-value="value"
                  :disabled="!isRowEditable(slotProps.data.id)"
                  placeholder="Select"
                />
              </div>
              <div class="flex flex-col">
                <p>Top 3:</p>
                <Dropdown
                  v-model="slotProps.data.cylinderNeedlesTop3"
                  :options="dialNeedles"
                  option-label="name"
                  option-value="value"
                  :disabled="!isRowEditable(slotProps.data.id)"
                  placeholder="Select"
                />
              </div>
              <div class="flex flex-col">
                <p>Bottom 4:</p>
                <Dropdown
                  v-model="slotProps.data.cylinderNeedlesBottom4"
                  :options="dialNeedles"
                  option-label="name"
                  option-value="value"
                  :disabled="!isRowEditable(slotProps.data.id)"
                  placeholder="Select"
                />
              </div>
            </div>
          </template>
        </Column>
        <Column field="" header="Actions" style="width: 21%">
          <template #body="slotProps">
            <div v-if="!isRowEditable(slotProps.data.id) && !slotProps.data.isNew" class="flex gap-1">
              <Button
                v-ripple
                outlined
                size="small"
                class="flex-1 gap-x-2"
                @click="handleEnableEdit(slotProps.data)"
                :loading="loading"
                :disabled="false"
              >
                <span class="text-xs">Edit</span>
                <Icon icon="basil:edit-outline" class="text-lg"/>
              </Button>
              <Button
                v-ripple
                outlined
                severity="danger"
                size="small"
                class="flex-1 gap-x-2"
                @click="handleDelete(slotProps.data.id!)"
                :loading="loading"
                :disabled="false"
              >
                <span class="text-xs">Delete</span>
                <Icon icon="basil:trash-outline" class="text-lg"/>
              </Button>
            </div>
            <div v-else class="flex gap-1">
              <Button
                v-ripple
                outlined
                size="small"
                class="flex-1 gap-x-2"
                @click="handleSave(slotProps.data.id)"
                :loading="loading"
                :disabled="false"
              >
                <span class="text-xs">Save</span>
                <Icon icon="basil:check-outline" class="text-xl"/>
              </Button>
            </div>
          </template>
        </Column>
      </DataTable>

  <div class="px-4 py-2 mt-2">
    <Button
      v-ripple outlined class="gap-x-2 w-full sm:w-auto"
      @click="addNewKnittingSetOutRow()"
      :loading="loading"
      :disabled="isAddingNew"
    >
      <span>Add New Knitting Set Out</span>
      <Icon icon="basil:plus-solid" class="text-2xl"/>
    </Button>
  </div>
</template>

<style scoped>
:deep(.p-datatable-table) {
  table-layout: fixed !important;
  width: 100% !important;
}

:deep(.p-inputtext),
:deep(.p-dropdown),
:deep(.p-inputnumber) {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

:deep(.p-dropdown-panel) {
  max-width: 100% !important;
}

:deep(.p-button) {
  min-width: auto !important;
  padding: 0.5rem !important;
}

:deep(.p-datatable-tbody > tr > td) {
  padding: 0.5rem !important;
  vertical-align: middle !important;
}

:deep(.p-datatable-thead > tr > th) {
  padding: 0.5rem !important;
  vertical-align: middle !important;
}
</style>